<script setup lang="ts">
type Props = {
  allowRedirect?: boolean;
  alwaysIcon?: boolean;
  alwaysLogo?: boolean;
  customSize?: boolean;
  showGift?: boolean;
  spanText?: string;
};

const {
  allowRedirect,
  alwaysIcon,
  alwaysLogo,
  customSize,
  showGift,
  spanText = '',
} = defineProps<Props>();

const { appearance } = storeToRefs(useAppearanceStore());
const { isAuthenticated } = storeToRefs(useAuthStore());

const logoImageUrl = computed(() => {
  return getAssetsUrlForCDN('/assets/images/logo.avif');
});

const iconImageUrl = computed(() => {
  return getUrlForApiImage(appearance.value.icon_img);
});
</script>

<template>
  <img
    v-if="alwaysIcon && iconImageUrl"
    :alt="appName()"
    :class="[$style.onlyIcon, { [$style.defaultSize]: !customSize }]"
    height="28"
    :src="iconImageUrl"
    width="28"
  />

  <div
    v-else
    :class="[$style.logoContainer, { [$style.defaultSize]: !customSize }]"
  >
    <NuxtLink
      :aria-label="`${appName()}`"
      :class="[$style.logoLink, { [$style.defaultSize]: !customSize }]"
      no-prefetch
      :to="allowRedirect ? localeLink('/') : ''"
    >
      <img
        v-if="logoImageUrl"
        :alt="appName()"
        :class="$style.desktop"
        height="28"
        :src="logoImageUrl"
        width="140"
      />

      <img
        v-if="isAuthenticated && iconImageUrl && !alwaysLogo"
        :alt="appName()"
        :class="$style.mobile"
        height="24"
        :src="iconImageUrl"
        width="24"
      />

      <img
        v-else-if="logoImageUrl"
        :alt="appName()"
        :class="[$style.mobile]"
        height="24"
        :src="logoImageUrl"
        width="120"
      />

      <span v-if="spanText">
        {{ spanText }}
      </span>
    </NuxtLink>

    <SmarticoBaseGiftIcon v-if="showGift" />
  </div>
</template>

<style module lang="postcss">
.logoContainer {
  @apply inline-flex items-center gap-2 mr-0 md:mr-6 min-h-4;

  &.defaultSize {
    @apply h-6 w-full md:h-7;
  }

  span {
    @apply hidden;
  }

  .logoLink {
    @apply h-full flex object-contain;

    &.defaultSize {
      @apply h-6 md:h-7;
    }

    .desktop {
      @apply h-full hidden sm:flex object-contain;
    }

    .mobile {
      @apply h-full flex object-contain sm:hidden;
    }
  }
}

.onlyIcon {
  @apply inline-flex;

  &.defaultSize {
    @apply h-6 w-full md:h-7;
  }
}
</style>
